import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form from "./base"
import Calc from "@/common/util/calculator"

export default class EllipseForm extends Form {
  protected path(width: number, height: number) {
    const commands = this.commands(width, height)

    return new PathObject(commands.join(""), this.defaultParams)
  }

  protected commands(width: number, height: number) {
    const startY = -(height / 2)
    const rx = width / 2
    const ry = height / 2

    const commands = [this.pathMoveTo(0, startY)]

    commands.push(this.pathArcAbs(rx, ry, width / 2, 0))
    commands.push(this.pathArcAbs(rx, ry, 0, height / 2))
    commands.push(this.pathArcAbs(rx, ry, -(width / 2), 0))
    commands.push(this.pathArcAbs(rx, ry, 0, startY))
    commands.push(this.pathClose())

    return commands
  }

  protected cutlineGappedPath(width: number): DrawableMixin | undefined {
    if (["label_plates", "stainless_steel_sign"].includes(this.signType) && this.state.material === "stainless") {
      const commands = this.circleCutLine(width)

      const path = new PathObject(commands, {
        ...this.defaultParams,
        stroke: "#f08894",
        strokeWidth: this.drawables.cutlineWidth,
        fill: "transparent",
      })

      return path
    }

    return
  }

  protected snappablePaths(width: number, height: number): [DrawableMixin, DrawableMixin] {
    const gap = Calc.mmToPx(3)
    const rx = width / 2
    const ry = height / 2

    if (width > height) {
      const side = [
        this.pathMoveTo(-(gap / 2), height / 2),
        this.pathArcAbs(rx, ry, gap / 2, height / 2, false), // B
        this.pathMoveTo(gap / 2, -(height / 2)),
        this.pathArcAbs(rx, ry, -(gap / 2), -(height / 2), false),
      ]

      const rest = [
        this.pathMoveTo(-(width / 2), 0),
        this.pathArcAbs(rx, ry, -(gap / 2), height / 2, false), // BL
        this.pathMoveTo(gap / 2, height / 2),
        this.pathArcAbs(rx, ry, width / 2, 0, false), // BR
        this.pathArcAbs(rx, ry, gap / 2, -(height / 2), false), // TR
        this.pathMoveTo(-(gap / 2), -(height / 2)),
        this.pathArcAbs(rx, ry, -(width / 2), 0, false), // TL
      ]

      return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
    }

    const side = [
      this.pathMoveTo(-(width / 2), -(gap / 2)),
      this.pathArcAbs(rx, ry, -(width / 2), gap / 2, false), // L
      this.pathMoveTo(width / 2, gap / 2),
      this.pathArcAbs(rx, ry, width / 2, -(gap / 2), false), // R
    ]

    const rest = [
      this.pathMoveTo(-(width / 2), gap / 2),
      this.pathArcAbs(rx, ry, 0, height / 2, false), // BL
      this.pathArcAbs(rx, ry, width / 2, gap / 2, false), // BR
      this.pathMoveTo(width / 2, -(gap / 2)),
      this.pathArcAbs(rx, ry, 0, -(height / 2), false), // TR
      this.pathArcAbs(rx, ry, -(width / 2), -(gap / 2), false), // TL
    ]

    return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
  }

  protected circleCutLine(width: number): string {
    const radius = width / 2

    const gapWidthPx = Calc.mmToPx(0.5)
    const legLengthPx = Calc.mmToPx(2.5)

    const segments = 3
    let segmentAngle = 360 / segments
    const startAngle = 90
    const gapAngle = (gapWidthPx * 360) / (2 * Math.PI * radius)
    segmentAngle -= gapAngle // remove gap from segment space

    const commands = []

    for (let i = 0; i < segments; i++) {
      const arcStart = startAngle + i * (segmentAngle + gapAngle)
      const arcEnd = arcStart + segmentAngle

      const arcStartRad = (arcStart * Math.PI) / 180
      const arcEndRad = (arcEnd * Math.PI) / 180

      const legStartX = (radius + legLengthPx) * Math.cos(arcStartRad)
      const legStartY = (radius + legLengthPx) * Math.sin(arcStartRad)

      // circle edge
      const arcStartX = radius * Math.cos(arcStartRad)
      const arcStartY = radius * Math.sin(arcStartRad)
      const arcEndX = radius * Math.cos(arcEndRad)
      const arcEndY = radius * Math.sin(arcEndRad)

      // leg + arc
      commands.push(this.pathMoveTo(legStartX, legStartY))
      commands.push(this.pathLineToAbs(arcStartX, arcStartY))
      commands.push(this.pathArcAbs(radius, radius, arcEndX, arcEndY, true))

      if (i < segments - 1) {
        const nextArcStart = arcEnd + gapAngle
        const nextArcStartRad = (nextArcStart * Math.PI) / 180
        const nx = (radius + legLengthPx) * Math.cos(nextArcStartRad)
        const ny = (radius + legLengthPx) * Math.sin(nextArcStartRad)
        commands.push(this.pathMoveTo(nx, ny))
      }
    }

    return commands.join("")
  }
}
